"use client";

import React from "react";
import { Package, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Link from "next/link";
import { useOfferingHooks } from "@/lib/hooks/api/offerings/use-offerings-hooks";
import { LinkedEntityContextShell } from "./LinkedEntityContextShell";
import {
  getLinkedEntityFullPageHref,
  getTaskLinkedEntityId,
  normalizeTaskLinkedEntityType,
} from "@/lib/utils/crm/task-linked-entity";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

/**
 * Linked offering
 */
export function TaskLinkedOfferingPanel({ item }: TabContentProps<ContactTaskRow>) {
  const offeringId = getTaskLinkedEntityId(item);
  const enabled = normalizeTaskLinkedEntityType(item.entity_type) === "offerings" && !!offeringId;
  const offeringHooks = useOfferingHooks();
  const { data: offering, isLoading, error } = offeringHooks.useSingle(offeringId ?? "", { enabled });

  if (!enabled) return null;

  return (
    <LinkedEntityContextShell task={item} expectedKind="offerings">
      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Loading offering…</p>
          </CardContent>
        </Card>
      ) : error || !offering ? (
        <Card>
          <CardContent className="py-8 text-center text-red-600 dark:text-red-400">
            <p>Could not load this offering.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="w-full space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <h3 className="text-lg font-semibold text-card-foreground">Offering</h3>
            </div>
            <Button asChild variant="outline" size="sm" className="gap-2">
              <Link href={getLinkedEntityFullPageHref("offerings", offering.id)}>
                <ExternalLink className="w-4 h-4" />
                View offerings
              </Link>
            </Button>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{(offering as { title?: string }).title ?? "Offering"}</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}
    </LinkedEntityContextShell>
  );
}
